import { ChangeDetectorRef, Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Prefs } from '@df/session/prefs';
import { PriceService } from '@df/ui/price/price.service';
import { TranslationService } from '@df/ui/translation/translation.service';
import { Subscription } from 'rxjs';

@Directive()
export abstract class PriceComponentDef implements OnInit, OnDestroy, OnChanges {
    protected translationService = inject(TranslationService);

    protected subscriptions = new Subscription();

    /**
     * Buffer for value setter
     */
    protected _value: number | undefined;

    /**
     * Buffer for precision setter
     */
    protected _precision?: number | string;

    /**
     * Numeric price value
     * @type {number}
     */
    @Input()
    set value(value: number | undefined) {
        this._value = value;
    }

    @Input()
    useFreeLabel = false;

    /**
     * Price precision
     * Either number or string 'auto'
     * @type {number | string}
     */
    @Input()
    set precision(precision: number | string) {
        this._precision = precision;
    }

    @Input()
    currencySymbol?: string;

    protected priceService = inject(PriceService);
    protected prefs = inject(Prefs);

    /**
     * Subscription
     */
    protected subscription?: Subscription;

    /**
     * class constructor
     */
    constructor(protected _ref: ChangeDetectorRef) {
        this._ref.detach();
    }

    /**
     * Listen to currency changes
     */
    ngOnInit() {
        this.subscriptions.add(this.prefs.obs.currency.subscribe(() => this.detectChanges()));
    }

    ngOnChanges(changesObj: SimpleChanges) {
        this.detectChanges();
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    /**
     * Gets component html
     */
    getHtml(): string | undefined {
        if (this.useFreeLabel && this._value === 0) {
            return this.priceService.freeLabel;
        }
        if (this._value || this._value === 0) {
            return this.priceService.toHtmlString(this._value, this._precision, this.currencySymbol);
        }
        return '';
    }

    getText(): string | undefined {
        if (this.useFreeLabel && this._value === 0) {
            return this.translationService.translate(this.priceService.freeLabel);
        }
        if (this._value || this._value === 0) {
            return this.priceService.toPlainString(this._value, this._precision, this.currencySymbol);
        }
        return '';
    }

    protected detectChanges(): void {
        this._ref.detectChanges();
    }
}
